/**
 * Sandeep: Azure configuration.
 * [09/08/2023]
 */
import { AuthenticationResult, EventMessage, EventType, LogLevel, PublicClientApplication } from "@azure/msal-browser";
import axios from "axios";

/**
 * Sandeep: Configuration object for instentiating msal library.
 * Loggin can be adjusted using on log level.
 */
export const msalConfig = {
    auth: msalConfigAuthOptions,
    cache: {
        cacheLocation: "localStorage",
        storeAuthStateInCookie: true
    },
    system: {
        loggerOptions: {
            logLevel: LogLevel.Error,
            loggerCallback: (level: any, message: any, containsPii: any) => {
                if (containsPii) {
                    return;
                }
                switch (level) {
                    case LogLevel.Error:
                        console.error(message);
                        return;
                    case LogLevel.Info:
                        console.info(message);
                        return;
                    case LogLevel.Verbose:
                        console.debug(message);
                        return;
                    case LogLevel.Warning:
                        console.warn(message);
                        return;
                    default:
                        return;
                }
            },
            piiLoggingEnabled: true
        }
    }
};

/**
 * Sandeep: Login request used for login and providing scopes while getting access token.
 */
export const loginRequest = {
    scopes: ['profile', 'openid', 'offline_access', msalConfigAuthOptions.clientId],
    extraQueryParameters: {
        ui_locales: 'en-us',
        "domain": "TOP"//Sandeep: Additional parameter for custom policies.
    },
    state: "TOP" //Sandeep: State passed is returned as is after login.
};
/**
 * Sandeep: For calling Microsoft graph API.
 */
export const graphConfig = {
    graphMeEndpoint: "https://graph.microsoft.com/me" //e.g. https://graph.microsoft.com/v1.0/me
};

/**
 * Sandeep: MSAL library initilization including event handling 
 * and setting active account for authetication and getting token.
 * @param msalInstance
 */
export const initilizeMsal = (msalInstance: PublicClientApplication) => {
    msalInstance.initialize().then(() => {
        // Account selection logic is app dependent. Adjust as needed for different use cases.
        const accounts = msalInstance.getAllAccounts();
        if (accounts.length > 0) {
            msalInstance.setActiveAccount(accounts[0]);
        }

        msalInstance.addEventCallback((event: EventMessage) => {

            if (event.eventType === EventType.LOGIN_SUCCESS && event.payload) {
                const payload = event.payload as AuthenticationResult;
                const account = payload.account;
                msalInstance.setActiveAccount(account);
            }
        });
    });
}

/**
 * Sandeep: Call this to acquire access token once the user has logged in.
 * @param msalInstance
 * @returns
 */
export const acquireAccessToken = async (msalInstance: PublicClientApplication) => {
    const activeAccount = msalInstance.getActiveAccount(); // This will only return a non-null value if you have logic somewhere else that calls the setActiveAccount API
    const accounts = msalInstance.getAllAccounts();

    if (!activeAccount && accounts.length === 0) {
        /*
        * Sandeep[09/08/23]: User is not signed in. Throw error or wait for user to login.
        * Do not attempt to log a user in outside of the context of MsalProvider
        */
        return null;
    }

    /**
     * Sandeep[09/08/23]:Passing client ID of the app registered in B2C portal to the scopes array. 
     * Not sure of the ramifications or why this works but it worked. 
     * I think we need to expose an API and add a custom scope for a resource if we
     * don't want to pass the clientId to get an accessToken for that resource otherwise.
     */
    const request = {
        scopes: loginRequest.scopes,
        account: activeAccount || accounts[0],
    };

    const authResult = await msalInstance.acquireTokenSilent(request);
    return authResult.accessToken;
};